/**
 * 사용자 사이드바(menus.path / sort_order)와 한도 query_type 정렬 연동
 * — 환경설정(Settings)과 동일한 그룹 순서
 */

const MENU_GROUP_ORDER = ['base', 'naver', 'tool'];

/**
 * 메뉴 path → 해당 화면에 묶인 query_type 목록
 * (키워드 Ver1은 기본 순위 keywordVer1 + 통검 keyword1 분리)
 */
export function queryTypesFromMenuPath(path) {
    if (!path || typeof path !== 'string') return [];
    const p = path.trim().toLowerCase().replace(/\/+$/, '') || '/';
    const map = {
        '/keywordver1': ['keywordVer1', 'keyword1'],
        '/keywordver2': ['keywordVer2'],
        '/naverintegratedrank': ['naverIntegratedRank'],
        '/keywordrankvolumecheck': ['keywordRankVolumeCheck'],
        '/findmid': ['findMid'],
        '/findproductbymid': ['findProductByMid'],
        '/counttext': ['countText'],
        '/naversaleanalysis': ['sales'],
        '/naverindexanalysis': ['index'],
        '/findnaverrank': ['findNaverRank'],
        '/findnaverranktracking': ['findNaverRankTracking'],
        '/changeurl': ['changeUrl'],
        '/integratedsearch': ['integratedSearch'],
        '/product-analysis': ['productAnalysis'],
        '/productgenerate': ['productGenerate'],
        '/rankhistorywatch': ['rankHistoryWatch'],
        '/autocomplete': ['autoComplete'],
        '/dashboard': [],
        '/main': [],
    };
    return Object.prototype.hasOwnProperty.call(map, p) ? map[p] : [];
}

/** 하위 호환: 첫 번째 query_type만 (한도 없는 메뉴는 null) */
export function queryTypeFromMenuPath(path) {
    const q = queryTypesFromMenuPath(path);
    return q.length ? q[0] : null;
}

export function sortMenusLikeSettings(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const rank = grp => {
        const g = String(grp ?? '')
            .trim()
            .toLowerCase();
        const i = MENU_GROUP_ORDER.indexOf(g);
        return i >= 0 ? i : 999;
    };
    return [...rows].sort((a, b) => {
        const rg = rank(a.menu_group) - rank(b.menu_group);
        if (rg !== 0) return rg;
        const ro = (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
        if (ro !== 0) return ro;
        return String(a.menu_key ?? '').localeCompare(String(b.menu_key ?? ''));
    });
}

/** 메뉴 행에서 한도 타입이 있는 것만, 사이드바 순서대로 중복 제거 */
export function queryTypesOrderFromMenus(menuRows) {
    const sorted = sortMenusLikeSettings(menuRows);
    const seen = new Set();
    const out = [];
    for (const m of sorted) {
        const qts = queryTypesFromMenuPath(m.path);
        for (const qt of qts) {
            if (qt && !seen.has(qt)) {
                seen.add(qt);
                out.push(qt);
            }
        }
    }
    return out;
}

/**
 * limitConfig 항목을 DB 메뉴 순서로 재배열.
 * 메뉴에 없는 query_type은 limitConfig 원래 순서대로 뒤에 붙음.
 */
export function orderLimitConfigByMenus(menuRows, baseLimitConfig) {
    if (!Array.isArray(baseLimitConfig) || baseLimitConfig.length === 0) {
        return [];
    }
    if (!Array.isArray(menuRows) || menuRows.length === 0) {
        return [...baseLimitConfig];
    }
    
    const nameMap = {};
    const sorted = sortMenusLikeSettings(menuRows);
    for (const m of sorted) {
        const qts = queryTypesFromMenuPath(m.path);
        for (const qt of qts) {
            if (qt && !nameMap[qt] && m.label) {
                nameMap[qt] = { label: m.label, count: qts.length };
            }
        }
    }

    const order = queryTypesOrderFromMenus(menuRows);
    const byKey = Object.fromEntries(baseLimitConfig.map(c => [c.key, c]));
    
    const ordered = order.map(k => {
        if (byKey[k]) {
            let finalLabel = byKey[k].label;
            const mapped = nameMap[k];
            if (mapped) {
                if (mapped.count > 1) {
                    let suffix = '';
                    const orig = byKey[k].label || '';
                    if (orig.includes('>')) {
                        suffix = orig.split('>').pop().trim();
                    } else if (orig.includes('(')) {
                        const m = orig.match(/(\([^)]+\))/);
                        if (m) suffix = m[1];
                    }
                    // if custom label already contains the suffix, don't duplicate it
                    if (suffix && !mapped.label.includes(suffix)) {
                        finalLabel = `${mapped.label} - ${suffix}`;
                    } else {
                        finalLabel = mapped.label;
                    }
                } else {
                    finalLabel = mapped.label;
                }
            }
            return { ...byKey[k], label: finalLabel };
        }
        return null;
    }).filter(Boolean);
    
    return ordered;
}

/**
 * menus.rank_row_gubun (쉼표·세미콜론 구분) → 사이드바 순서대로 중복 제거된 { gubun, label }[]
 */
export function parseRankRowGubunsFromMenus(menuRows) {
    if (!Array.isArray(menuRows) || menuRows.length === 0) return [];
    const sorted = sortMenusLikeSettings(menuRows);
    const seen = new Set();
    const out = [];
    for (const m of sorted) {
        const raw = m.rank_row_gubun;
        if (raw == null) continue;
        const s = String(raw).trim();
        if (!s) continue;
        const parts = s
            .split(/[,;]+/)
            .map(x => x.trim())
            .filter(Boolean);
        for (const gubun of parts) {
            if (!/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/.test(gubun)) continue;
            if (seen.has(gubun)) continue;
            seen.add(gubun);
            out.push({
                gubun,
                label: `${m.label || m.menu_key} · ${gubun}`,
            });
        }
    }
    return out;
}

/**
 * 사용자관리「테이블 사용 행 수」목록: menus.rank_row_gubun 만 사용 (path·limitConfig 폴백 없음)
 */
export function getTableRowLimitEntriesForAdmin(menuRows) {
    return parseRankRowGubunsFromMenus(menuRows);
}
